import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { brandsService } from "../../services/brands";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import { trackEvent } from "../../utils/tracking";
import { parsePaginatedResponse } from "../../utils/pagination";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { useToast } from "../../contexts/ToastContext";
import AdaptGenerateIdeas from "./adaptGenerateIdeas";
import AdaptScript from "./adaptScript";

const DEFAULT_PROMOTION_SCORE = 5;
const ADAPT_GENERATE_IDEAS = "generate-ideas";

const compareByRecentDate = (a, b) =>
  new Date(b?.create_date || 0).getTime() - new Date(a?.create_date || 0).getTime();

const clampPromotionScore = (value, fallback = DEFAULT_PROMOTION_SCORE) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(10, Math.round(parsed)));
};

/**
 * Brand scripts flow: Generate Ideas → Write Script.
 * Uses the workspace current brand (no brand picker on this page).
 */
const Adapt = ({ baseUrl, pattern, patternTypeLabel, patternType, audience, patternRequestId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentBrand } = useSelector((state) => state.brand);
  const baseUrlNorm = baseUrl?.endsWith("/") ? baseUrl : `${baseUrl || ""}/`;
  const baseUrlSlash = baseUrlNorm.endsWith("/") ? baseUrlNorm : `${baseUrlNorm}/`;
  const pathname = location?.pathname || "";
  const searchSuffix = location?.search || "";

  const scriptMatch = pathname.match(/\/scripts\/script\/([^/]+)/);
  const scriptHashFromUrl = scriptMatch ? scriptMatch[1] : null;

  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(true);
  const [generatingForBrand, setGeneratingForBrand] = useState(null);
  const [generatingScriptForIdea, setGeneratingScriptForIdea] = useState(null);
  const [brandIdeaPrompt, setBrandIdeaPrompt] = useState("");
  const [brandPromotionOverride, setBrandPromotionOverride] = useState(DEFAULT_PROMOTION_SCORE);
  const [brandNumIdeas, setBrandNumIdeas] = useState(3);
  const [scriptPromptsByIdea, setScriptPromptsByIdea] = useState({});
  const [scriptPromotionByIdea, setScriptPromotionByIdea] = useState({});
  const [modifyOpenForIdea, setModifyOpenForIdea] = useState(null);
  const [viewingScript, setViewingScript] = useState(null);
  const { showToast } = useToast();

  const brandHash = currentBrand?.hash;

  useEffect(() => {
    if (!brandHash) return;
    setBrandPromotionOverride(
      clampPromotionScore(currentBrand?.promotion_rating, DEFAULT_PROMOTION_SCORE)
    );
  }, [brandHash, currentBrand?.promotion_rating]);

  const fetchIdeas = useCallback(() => {
    if (!brandHash || patternRequestId == null) {
      setIdeas([]);
      setLoadingIdeas(false);
      return;
    }
    setLoadingIdeas(true);
    brandsService.listIdeas(
      {
        pattern_request_id: patternRequestId,
        pattern_key: pattern?.id ?? pattern?.slug ?? "",
        brand_hash: brandHash,
        page_size: 1000,
      },
      (ideasRes) => {
        const { results } = parsePaginatedResponse(ideasRes);
        setIdeas([...results].sort(compareByRecentDate));
        setLoadingIdeas(false);
      },
      () => {
        setIdeas([]);
        setLoadingIdeas(false);
      }
    );
  }, [brandHash, pattern?.id, pattern?.slug, patternRequestId]);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  const isOnAdaptRoot = pathname.replace(/\/$/, "") === baseUrlSlash.replace(/\/$/, "") || pathname === baseUrlSlash.slice(0, -1);
  const hasAdaptSubRoute = pathname.includes(`/${ADAPT_GENERATE_IDEAS}`) || pathname.includes("/script/");

  useEffect(() => {
    if (!baseUrlSlash || !isOnAdaptRoot || hasAdaptSubRoute || loadingIdeas) return;
    navigate(`${baseUrlSlash}${ADAPT_GENERATE_IDEAS}/${searchSuffix}`, { replace: true });
  }, [baseUrlSlash, isOnAdaptRoot, hasAdaptSubRoute, loadingIdeas, navigate]);

  useEffect(() => {
    if (!scriptHashFromUrl || viewingScript?.hash === scriptHashFromUrl) return;
    brandsService.getScript(
      scriptHashFromUrl,
      (res) => {
        const script = res?.data;
        if (script) setViewingScript(script);
      },
      () => {
        setViewingScript(null);
      }
    );
  }, [scriptHashFromUrl, viewingScript]);

  useEffect(() => {
    if (pathname.includes(`/${ADAPT_GENERATE_IDEAS}`) && !scriptHashFromUrl) {
      setViewingScript(null);
    }
  }, [pathname, scriptHashFromUrl]);

  const handleGenerateIdeas = () => {
    if (!brandHash) return;
    const userPrompt = brandIdeaPrompt.trim();
    const promotionScore = clampPromotionScore(brandPromotionOverride, DEFAULT_PROMOTION_SCORE);
    const count = Math.max(1, Math.min(10, Number(brandNumIdeas) || 3));
    setGeneratingForBrand(brandHash);
    brandsService.createIdeas(
      {
        brand_hash: brandHash,
        patterns: [
          {
            pattern_request_id: patternRequestId,
            pattern_key: pattern?.id ?? pattern?.slug ?? "",
          },
        ],
        count,
        promotion_score: promotionScore,
        user_prompt: userPrompt || undefined,
      },
      (res) => {
        const payload = res?.data ?? {};
        const created = Array.isArray(payload.ideas) ? payload.ideas : (Array.isArray(payload) ? payload : []);
        setIdeas((prev) => [...(created || []), ...prev].sort(compareByRecentDate));
        setGeneratingForBrand(null);
        trackEvent("generate_ideas", {
          brand_hash: brandHash,
          count,
          pattern_request_id: patternRequestId,
          pattern_type: patternType,
          pattern_type_label: patternTypeLabel,
          pattern_name: pattern?.name ?? pattern?.id ?? pattern?.slug,
        });
      },
      () => setGeneratingForBrand(null)
    );
  };

  const handleGenerateScript = (idea) => {
    setGeneratingScriptForIdea(idea.hash);
    const prompt = (scriptPromptsByIdea[idea.hash] || "").trim();
    const promotionScore = scriptPromotionByIdea[idea.hash];
    const payload = {
      idea_hash: idea.hash,
      prompt: prompt || undefined,
    };
    if (promotionScore != null) payload.promotion_score = promotionScore;
    brandsService.createScript(
      payload,
      (res) => {
        const script = res.data;
        if (script?.hash) {
          setViewingScript(script);
          trackEvent("generate_script", {
            idea_hash: idea.hash,
            script_hash: script.hash,
            source: "scripts",
            pattern_type: patternType,
            pattern_type_label: patternTypeLabel,
            pattern_name: pattern?.name ?? pattern?.id ?? pattern?.slug,
          });
          navigate(`${baseUrlSlash}script/${script.hash}/${searchSuffix}`, { replace: false });
        }
        setGeneratingScriptForIdea(null);
      },
      (err) => {
        setGeneratingScriptForIdea(null);
        showToast(getApiErrorMessage(err, "Could not create script. Please try again."), {
          type: "error",
        });
      }
    );
  };

  const handleToggleStar = (ideaHash, starred) => {
    setIdeas((prev) =>
      prev.map((idea) => (idea.hash === ideaHash ? { ...idea, starred } : idea))
    );
    brandsService.updateIdea(
      ideaHash,
      { starred },
      (res) => {
        const updated = res.data;
        if (!updated?.hash) return;
        setIdeas((prev) =>
          prev.map((idea) => (idea.hash === ideaHash ? { ...idea, ...updated } : idea))
        );
      },
      () => {
        setIdeas((prev) =>
          prev.map((idea) =>
            idea.hash === ideaHash ? { ...idea, starred: !starred } : idea
          )
        );
      }
    );
  };

  if (!brandHash) {
    return (
      <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-6 text-theme-text opacity-80">
        Select a brand to generate scripts from this pattern.
      </div>
    );
  }

  if (loadingIdeas) {
    return (
      <div className="flex justify-center py-8 text-theme-text">
        <OrbitalLoader size={168} text="Loading ideas..." />
      </div>
    );
  }

  if (scriptHashFromUrl && viewingScript) {
    return (
      <AdaptScript
        baseUrlSlash={baseUrlSlash}
        script={viewingScript}
        brand={currentBrand}
        onScriptRegenerated={(newScript) => {
          setViewingScript(newScript);
          navigate(`${baseUrlSlash}script/${newScript.hash}/${searchSuffix}`, { replace: false });
        }}
      />
    );
  }

  if (scriptHashFromUrl && !viewingScript) {
    return (
      <div className="flex justify-center py-8 text-theme-text">
        <OrbitalLoader size={72} text="Loading script..." />
      </div>
    );
  }

  if (pathname.includes("/add-brand")) {
    return <Navigate to={`${baseUrlSlash}${ADAPT_GENERATE_IDEAS}/${searchSuffix}`} replace />;
  }

  const isGenerateIdeas = pathname.includes(`/${ADAPT_GENERATE_IDEAS}`);

  if (isOnAdaptRoot && !hasAdaptSubRoute) {
    return <Navigate to={`${baseUrlSlash}${ADAPT_GENERATE_IDEAS}/${searchSuffix}`} replace />;
  }

  if (isGenerateIdeas) {
    return (
      <AdaptGenerateIdeas
        baseUrlSlash={baseUrlSlash}
        pattern={pattern}
        patternTypeLabel={patternTypeLabel}
        patternType={patternType}
        brand={currentBrand}
        ideas={ideas}
        brandIdeaPrompt={brandIdeaPrompt}
        brandPromotionOverride={brandPromotionOverride}
        brandNumIdeas={brandNumIdeas}
        onBrandIdeaPromptChange={setBrandIdeaPrompt}
        onBrandPromotionChange={setBrandPromotionOverride}
        onBrandNumIdeasChange={setBrandNumIdeas}
        onGenerateIdeas={handleGenerateIdeas}
        onGenerateScript={handleGenerateScript}
        generatingForBrand={generatingForBrand}
        generatingScriptForIdea={generatingScriptForIdea}
        scriptPromptsByIdea={scriptPromptsByIdea}
        onScriptPromptChange={(hash, v) => setScriptPromptsByIdea((prev) => ({ ...prev, [hash]: v }))}
        scriptPromotionByIdea={scriptPromotionByIdea}
        onScriptPromotionChange={(hash, v) => setScriptPromotionByIdea((prev) => ({ ...prev, [hash]: v }))}
        onToggleStar={handleToggleStar}
        tweakOpenForIdea={modifyOpenForIdea}
        onTweakOpenChange={setModifyOpenForIdea}
      />
    );
  }

  return null;
};

export default Adapt;
